import { FrameSizeObject, GlobalBlockTypes } from "./types";

export const HomePageName = "home";

type LabelKeys = (typeof labels)[number];
export type ReceiptLabel = Record<LabelKeys, string>;

const labels = [
    "date",
    "time",
    "sale",
    "servedBy",
    "customerName",
    "customerAddress",
    "taxId",
    "station",
    "location",
    "table",
    "customText",
    "itemDiscount",
    "orderNotes",
    "returnPolicy",
    "subtotal",
    "shipping",
    "totalDiscount",
    "totalRedeemPoint",
    "total",
    "productTotal",
    "cartDiscount",
    "itemFee",
    "cartFee",
    "totalFee"
] as const;

export enum SupportedBarcodeTypes {
    code128 = "code 128",
    code39 = "code 39",
    code93 = "code 93",
    rationalizedCodabar = "codabar",
    ean5 = "ean 5",
    ean8 = "ean 8",
    code32 = "code 32",
    pharmacode = "pharmacode",
    msi = "msi",
    itf14 = "itf 14",
    code2of5 = "code 2 of 5",
    qrcode = "QR Code",
    microqrcode = "micro QR Code",
    upca = "UPC A",
    upce = "UPC E"
}

export const label = Object.fromEntries(
    labels.map(key => [
        key,
        key.charAt(0).toUpperCase() +
            key
                .slice(1)
                .replace(/([A-Z])/g, " $1")
                .trim()
    ])
) as ReceiptLabel;

export const buildSizes: Record<string, FrameSizeObject> = {
    fullHD: { label: "HD", value: { x: 1920, y: 1080 }, grouping: "1", type: "Desktop" },
    imac: { label: "iMac", value: { x: 2240, y: 1260 }, grouping: "1", type: "Desktop", class: "imac" },
    "58MM": {
        label: "58mm thermal print",
        value: { x: 219, y: "fit-content" },
        grouping: "1",
        type: GlobalBlockTypes.GB_PRINT
    },
    "80MM": {
        label: "80mm thermal print",
        value: { x: 301, y: "fit-content" },
        grouping: "1",
        type: GlobalBlockTypes.GB_PRINT
    },
    letterhead: { label: "Letterhead", value: { x: 815, y: 1055 }, grouping: "1", type: GlobalBlockTypes.GB_PRINT },
    a4: { label: "A4", value: { x: 793, y: 1121 }, grouping: "1", type: GlobalBlockTypes.GB_PRINT },
    "1x2Label": { label: "1x2 inch", value: { x: 95, y: 191 }, grouping: "2", type: GlobalBlockTypes.GB_PRINT },
    "1x3Label": { label: "1x3 inch", value: { x: 95, y: 287 }, grouping: "2", type: GlobalBlockTypes.GB_PRINT },
    "2x4Label": { label: "2x4 inch", value: { x: 191, y: 383 }, grouping: "2", type: GlobalBlockTypes.GB_PRINT },
    "2x6Label": { label: "2x6 inch", value: { x: 191, y: 575 }, grouping: "2", type: GlobalBlockTypes.GB_PRINT },
    iPhone17ProMax: {
        grouping: "1",
        label: "iPhone 17 Pro Max",
        class: "iphone-17",
        value: { x: 440, y: 956 },
        type: "Mobile"
    },
    iPhone17: {
        grouping: "1",
        label: "iPhone 17",
        class: "iphone-17",
        value: { x: 402, y: 874 }, 
        type: "Mobile"
    },
    iPhone16ProMax: {
        grouping: "1",
        label: "iPhone 16 Pro Max",
        class: "iphone-16",
        value: { x: 440, y: 956 },
        type: "Mobile"
    },
    iPhone16: {
        grouping: "1",
        label: "iPhone 16",
        class: "iphone-16",
        value: { x: 393, y: 852 },
        type: "Mobile"
    },
    iPhone15: {
        grouping: "1",
        label: "iPhone 15",
        class: "iphone-15",
        value: { x: 393, y: 852 },
        type: "Mobile"
    },

    iPhone14ProMax: {
        grouping: "1",
        label: "iPhone 14 Pro Max",
        class: "iphone-15",
        value: { x: 393, y: 932 },
        type: "Mobile"
    },

    iPhone14Pro: {
        grouping: "1",
        label: "iPhone 14 Pro",
        class: "iphone-15",
        value: { x: 393, y: 852 },
        type: "Mobile"
    },

    iPhone14: {
        grouping: "1",
        label: "iPhone 14",
        class: "iphone-14",
        value: { x: 390, y: 844 },
        type: "Mobile"
    },

    "iPadPro12.9": {
        grouping: "1",
        label: "iPad Pro 12.9",
        value: { x: 1024, y: 1366 },
        class: "ipad-large-flat",
        type: "Tablet"
    },
    "iPadAir10.9": {
        grouping: "1",
        label: "iPad Air 10.9",
        value: { x: 820, y: 1180 },
        type: "Tablet"
    },
    "iPad10.9": {
        grouping: "1",
        label: "iPad 10.9",
        value: { x: 810, y: 1080 },
        type: "Tablet"
    },
    "iPad10.2": {
        grouping: "1",
        label: "iPad 10.2",
        class: "ipad-10-2",
        value: { x: 810, y: 1080 },
        type: "Tablet"
    },
    "iPadMini8.3": {
        grouping: "1",
        label: "iPad mini 8.3",
        class: "ipad-mini",
        value: { x: 768, y: 1024 },
        type: "Tablet"
    },

    "samsungGalaxyTabS9+": {
        grouping: "1",
        label: "Samsung Galaxy Tab S9+",
        value: { x: 412, y: 846 },
        type: "Tablet"
    },
    samsungKiosk: {
        grouping: "1",
        label: "Samsung Kiosk",
        value: { x: 1080, y: 1920 },
        type: "Tablet",
        class: "samsung-kiosk",
        noRotate: true
    },

    amazonFireHD10: {
        grouping: "1",
        label: "Amazon Fire HD 10",
        value: { x: 1280, y: 800 },
        type: "Tablet"
    },
    "samsungGalaxyTabS7+": {
        grouping: "1",
        label: "Samsung Galaxy Tab S7+",
        value: { x: 1280, y: 800 },
        type: "Tablet"
    },
    samsungGalaxyTabS7: {
        grouping: "1",
        label: "Samsung Galaxy Tab S7",
        value: { x: 800, y: 1280 },
        type: "Tablet"
    },
    huaweiMatePadPro: {
        grouping: "1",
        label: "Huawei MatePad Pro",
        value: { x: 1280, y: 800 },
        type: "Tablet"
    },

    samsungGalaxyS24Ultra: {
        grouping: "1",
        label: "Samsung Galaxy S24 Ultra",
        class: "samsung-galaxy",
        value: { x: 360, y: 780 },
        type: "Mobile"
    },
    samsungGalaxyS24: {
        grouping: "1",
        label: "Samsung Galaxy S24",
        class: "samsung-galaxy",
        value: { x: 360, y: 780 },
        type: "Mobile"
    },
    samsungGalaxyS23Ultra: {
        grouping: "1",
        label: "Samsung Galaxy S23 Ultra",
        class: "samsung-galaxy",
        value: { x: 360, y: 780 },
        type: "Mobile"
    },
    "samsungGalaxyS23+": {
        grouping: "1",
        label: "Samsung Galaxy S23+",
        class: "samsung-galaxy",
        value: { x: 360, y: 780 },
        type: "Mobile"
    },
    samsungGalaxyS23: {
        grouping: "1",
        label: "Samsung Galaxy S23",
        class: "samsung-galaxy",
        value: { x: 360, y: 780 },
        type: "Mobile"
    },

    samsungGalaxyS22Ultra: {
        grouping: "1",
        label: "Samsung Galaxy S22 Ultra",
        class: "samsung-galaxy",
        value: { x: 384, y: 824 },
        type: "Mobile"
    },
    "samsungGalaxyS22+": {
        grouping: "1",
        label: "Samsung Galaxy S22+",
        class: "samsung-galaxy",
        value: { x: 384, y: 824 },
        type: "Mobile"
    },
    samsungGalaxyS22: {
        grouping: "1",
        label: "Samsung Galaxy S22",
        class: "samsung-galaxy",
        value: { x: 360, y: 780 },
        type: "Mobile"
    },

    samsungGalaxyS21Ultra: {
        grouping: "1",
        label: "Samsung Galaxy S21 Ultra",
        class: "samsung-galaxy",
        value: { x: 412, y: 915 },
        type: "Mobile"
    },
    "samsungGalaxyS21+": {
        grouping: "1",
        label: "Samsung Galaxy S21+",
        class: "samsung-galaxy",
        value: { x: 360, y: 800 },
        type: "Mobile"
    },
    googlePixel8Pro: {
        grouping: "1",
        label: "Google Pixel 8 Pro",
        class: "google-pixel",
        value: { x: 448, y: 998 },
        type: "Mobile"
    },
    googlePixel8: {
        grouping: "1",
        label: "Google Pixel 8",
        class: "google-pixel",
        value: { x: 412, y: 915 },
        type: "Mobile"
    },
    googlePixel7Pro: {
        grouping: "1",
        label: "Google Pixel 7 Pro",
        class: "google-pixel",
        value: { x: 412, y: 892 },
        type: "Mobile"
    },

    googlePixel7: {
        grouping: "1",
        label: "Google Pixel 7",
        class: "google-pixel",
        value: { x: 412, y: 915 },
        type: "Mobile"
    },
    googlePixel6Pro: {
        grouping: "1",
        label: "Google Pixel 6 Pro",
        class: "google-pixel",
        value: { x: 412, y: 892 },
        type: "Mobile"
    },
    googlePixel6: {
        grouping: "1",
        label: "Google Pixel 6",
        class: "google-pixel",
        value: { x: 412, y: 915 },
        type: "Mobile"
    }
};

export enum QueryDataTables {
    PRODUCTS = "products",
    ORDERS = "orders",
    CUSTOMERS = "customers"
}

export enum QueryFieldsProducts {
    ALL = "all",
    ONSALE = "variants.isOnSale",
    CATEGORIES = "categories",
    NAME = "name",
    SKU = "sku",
    BARCODE = "variants.barcode"
}

export enum QueryOrderByOptionsProducts {
    NAME = "name",
    PRICE = "variants.price"
}

export enum QueryOrderByOptionsOrders {}

export enum QueryOrderByOptionsCustomers {}

export enum QueryFieldsCustomers {
    ALL = "all",
    FIRSTNAME = "firstName",
    LASTNAME = "lastName",
    EMAIL = "email"
}

export enum ClockAction {
    // Clock in
    EARLY_CLOCK_IN = "EARLY_CLOCK_IN",
    NORMAL_CLOCK_IN = "NORMAL_CLOCK_IN",
    LATE_CLOCK_IN = "LATE_CLOCK_IN",
    // Clock out
    EARLY_CLOCK_OUT = "EARLY_CLOCK_OUT",
    NORMAL_CLOCK_OUT = "NORMAL_CLOCK_OUT",
    LATE_CLOCK_OUT = "LATE_CLOCK_OUT",

    // Lunch time clock out
    EARLY_LUNCH_CLOCK_OUT = "EARLY_LUNCH_CLOCK_OUT",
    NORMAL_LUNCH_CLOCK_OUT = "NORMAL_LUNCH_CLOCK_OUT",
    LATE_LUNCH_CLOCK_OUT = "LATE_LUNCH_CLOCK_OUT",
    // Lunch time clock in
    EARLY_LUNCH_CLOCK_IN = "EARLY_LUNCH_CLOCK_IN",
    NORMAL_LUNCH_CLOCK_IN = "NORMAL_LUNCH_CLOCK_IN",
    LATE_LUNCH_CLOCK_IN = "LATE_LUNCH_CLOCK_IN"
}
